import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Loader2 } from 'lucide-react';
import './Contact.css';

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  projectBrief: string;
};

const PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID?.trim() ?? '';
const FORM_GUID = process.env.REACT_APP_HUBSPOT_FORM_GUID?.trim() ?? '';
const MESSAGE_FIELD =
  process.env.REACT_APP_HUBSPOT_MESSAGE_FIELD?.trim() || 'message';

function hubspotSubmitUrl(): string | null {
  if (!PORTAL_ID || !FORM_GUID) return null;
  return `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(
    PORTAL_ID
  )}/${encodeURIComponent(FORM_GUID)}`;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    projectBrief: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const url = hubspotSubmitUrl();
    if (!url) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    const payload = {
      submittedAt: Date.now(),
      fields: [
        { name: 'firstname', value: formData.firstName.trim() },
        { name: 'lastname', value: formData.lastName.trim() },
        { name: 'email', value: formData.email.trim() },
        { name: MESSAGE_FIELD, value: formData.projectBrief.trim() }
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title || 'Xenvota — Contact'
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', projectBrief: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">
            Get in touch with us. We are here to help you with your next idea, product, or digital
            transformation journey.
          </p>

          <div className="contact-grid">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="contact-info"
            >
              <h3>Get in Touch</h3>
              <p>
                We would love to hear from you. Reach out anytime and our team will respond as quickly
                as possible.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <Mail className="contact-icon" size={24} />
                  <div>
                    <h4>Email</h4>
                    <p>support@Xenvota.Com</p>
                  </div>
                </div>

                <div className="contact-item">
                  <Phone className="contact-icon" size={24} />
                  <div>
                    <h4>Phone</h4>
                    <p>+91 8800931207 | +91 9588288100</p>
                  </div>
                </div>

                <div className="contact-item">
                  <MapPin className="contact-icon" size={24} />
                  <div>
                    <h4>Address</h4>
                    <p>
                      Ganouli Chowk, Maniari, Darbhanga, Bihar - 846005, India
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="contact-form-container"
            >
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      autoComplete="given-name"
                      placeholder="First name"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      autoComplete="family-name"
                      placeholder="Last name"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    placeholder="your.email@example.com"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="projectBrief">Project brief</label>
                  <textarea
                    id="projectBrief"
                    name="projectBrief"
                    value={formData.projectBrief}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project..."
                    rows={5}
                    disabled={isSubmitting}
                  />
                </div>

                <motion.button
                  whileHover={isSubmitting ? undefined : { scale: 1.05 }}
                  whileTap={isSubmitting ? undefined : { scale: 0.95 }}
                  type="submit"
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  <span className="submit-btn-inner">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="submit-btn-loader" size={20} aria-hidden />
                        <span>Sending…</span>
                      </>
                    ) : (
                      <span>Request a quote</span>
                    )}
                  </span>
                </motion.button>

                {submitStatus === 'success' && (
                  <div className="success-message">Thank you! We&apos;ll get back to you soon.</div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
